
/// <amd-module name="Tfs/Charting/ChartDataModel" />
import { IVssPageContext } from 'VSS/Platform/Context';
import { IChartLegendSizeService } from "VSS/Features/Charts/ChartLegendSizeService";

import { ChartTelemetry } from "Tfs/Charting/ChartTelemetry";
import { IDataTableData } from "Tfs/Charting/ChartContracts";

import * as Resources from "Tfs/Charting/Resources";

var PublishCIData = true;

export class ChartConstants {
    public static oneDimensionalToken: string = "oneDimensional";
    public static keyName: string = "key";
    public static valueName: string = "value";
}

export interface ChartCommonDataOptions {
    width?: number;
    height?: number;
}

export class ChartCommonData {
    /** Describes a minimal contract of Chart data */
    constructor(options: ChartCommonDataOptions) {
        this._isReady = true;
        this.isResultEmpty = false;
        this._unreadyReason = undefined;
        this._unreadyIcon = undefined;

        if (options.width && options.height) {
            this.width = options.width;
            this.height = options.height;
        } else {
            this.markUnready(Resources.ChartSizeMissingMessage);
        }

    }

    public isReady(): Boolean {
        return this._isReady;
    }

    public getUnreadyReason(): string | undefined {
        return this._unreadyReason;
    }

    public getUnreadyIcon(): string {
        if (!this._unreadyIcon) {
            this._unreadyIcon = "icon-tfs-build-status-failed";
        }

        return this._unreadyIcon;
    }

    public markUnready(unreadyReason?: string, unreadyIcon?: string) {
        /** Marks the data set as unready. The error icon will be used by default if no icon name is specified. */
        this._isReady = false;
        this._unreadyReason = unreadyReason;
        this._unreadyIcon = unreadyIcon;
    }

    public getTestClasses(): Object {
        return {};
    }

    /** True if query result is empty. If query is not defined or result is not empty, returns false. */
    public isResultEmpty: boolean;
    public width: number;
    public height: number;
    public _isReady: boolean;
    public _unreadyReason: string | undefined;
    public _unreadyIcon: string | undefined;
}

export interface TabularRenderDataOptions extends ChartCommonDataOptions {
    seriesThreshold?: number;
    groupByThreshold?: number;
    transpose?: boolean;
    useSeriesColoring?: boolean;
}

export class TabularRenderData extends ChartCommonData {
    /** 
     * Tabular Render data operates on expectation of a complete (non-sparse) set of consistently sized series elements which form a rectangular table in Row Major order.
     * Series  === Each series corresponds to a Row
     * GroupBy === Each groupBy value corresponds to Columns within the rows
    **/
    private static _otherKey: string = Resources.ChartsRemainingItems;

    private _maxSeriesCount: number;
    private _maxGroupCount: number;

    // @ts-ignore
    private seriesHasOtherItem: boolean = false;

    // @ts-ignore
    private groupHasOtherItem: boolean = false;

    constructor(pageContext: IVssPageContext, options: TabularRenderDataOptions, dataset: IDataTableData[] | undefined) {
        super(options);

        if (this.isReady()) {
            if (dataset && dataset.length > 0 && String(dataset[0].value).length > 0) {
                const chartLegendSizeService: IChartLegendSizeService = pageContext.getService<IChartLegendSizeService>("IChartLegendSizeService");
                this._maxSeriesCount = (options && options.seriesThreshold) ? options.seriesThreshold : chartLegendSizeService.getDefaultSeriesLimit();
                this._maxGroupCount = (options && options.groupByThreshold) ? options.groupByThreshold : chartLegendSizeService.getDefaultGroupByLimit();
                dataset = TabularRenderData._transposeIfOneDimensional(dataset);
                if (options.transpose) {
                    dataset = TabularRenderData._transpose(dataset);
                }

                this._useSeriesColoring = options.useSeriesColoring;

                this._prepareNames(dataset);
                this._prepareValues(dataset);
                this._prepareTotals();

                if (PublishCIData) {
                    ChartTelemetry.OnModelReady(pageContext, dataset);
                    PublishCIData = false;
                }
            } else {
                this.isResultEmpty = true;
                this.markUnready(Resources.ChartMessage_NoDataToDisplay_Body);
            }
        }
    }

    private _getSeriesNames(dataset: IDataTableData[]): string[] {
        /** Get the series key names. */

        return this.getNames(dataset, this._maxSeriesCount, true);
    }

    private _getGroupNames(dataset: IDataTableData[]): string[] {
        /** Get the group key names from the first series. If the other rows have different sizes or orderings, the data will be malformed. */

        return this.getNames(dataset[0].value, this._maxGroupCount, false);
    }

    private getNames(dataset: any, threshold: number, seriesType: boolean): string[] {
        /** Get the series/group key names. */

        var names = [];
        for (var i = 0; i < dataset.length; i++) {
            if (i < threshold || (i === threshold && i === dataset.length - 1)) {
                names[i] = TabularRenderData.cleanKey(dataset[i].key);
            } else if (i === threshold) {
                names[i] = TabularRenderData.cleanKey(TabularRenderData._otherKey);
                seriesType ? this.seriesHasOtherItem = true : this.groupHasOtherItem = true;
            }
        }
        return names;
    }

    private _prepareNames(dataset: IDataTableData[]) {
        this.seriesNames = this._getSeriesNames(dataset);
        this.groupNames = this._getGroupNames(dataset);
    }

    private _prepareValues(dataset: IDataTableData[]) {
        var i, j, targetRow = 0;
        this.seriesValues = new Array<number[]>();

        for (i = 0; i < dataset.length; i++) {
            if (i <= this._maxSeriesCount) {
                this.seriesValues[i] = new Array<number>();
                targetRow = i;
                // Initialize each element
                for (j = 0; j < this.groupNames.length; j++) {
                    this.seriesValues[targetRow][j] = 0;
                }
            }
            else {
                targetRow = this._maxSeriesCount;
            }

            for (j = 0; j < dataset[i].value.length; j++) {
                var targetColumn: number = (j < this._maxGroupCount) ? j : this._maxGroupCount,
                    entry: number = dataset[i].value[j].value;
                this.seriesValues[targetRow][targetColumn] += entry;
            }
        }
    }

    private _prepareTotals() {
        /** Calculate the total values for the charts. */
        var i, j;

        this.seriesTotals = new Array();
        this.groupTotals = new Array();
        this.maxValue = 0;

        var rows = this.seriesNames.length;
        var cols = this.groupNames.length;

        for (j = 0; j < cols; j++) {
            this.groupTotals[j] = 0
        }

        for (i = 0; i < rows; i++) {
            for (j = 0; j < cols; j++) {
                var sample = this.seriesValues[i][j];
                this.groupTotals[j] += sample;
                this.maxValue = Math.max(sample, this.maxValue);
            }

            this.seriesTotals[i] = this.seriesValues[i].reduce(function (a: number, b: number) {
                return a + b;
            }, 0);
        }
        this.grandTotal = this.seriesTotals.reduce(function (a, b) {
            return a + b;
        }, 0);
    }

    public isOneDimensional(): boolean {
        /** 
         * Indicates if this table is intended for use as a single dimensional set.
         * Note: a data set with a single column can still be rendered as if it were multi-dimensional.
         */
        if (this.groupNames && this.groupNames.length > 0) {
            return this.groupNames[0] === ChartConstants.oneDimensionalToken;
        } else {
            return true;
        }
    }

    /** Exposes the names of elements used for coloring the chart */
    public getColoringDimensionNames(): string[] {
        return this.useSeriesColoring() ? this.seriesNames : this.groupNames;
    }

    public useSeriesColoring(): boolean | undefined {
        return this.isOneDimensional() || this._useSeriesColoring;
    }

    public getTestClasses(): Object {
        var classes: { [key: string]: any } = super.getTestClasses();
        classes["one-dimensional"] = this.isOneDimensional();
        return classes;
    }

    private static _transposeIfOneDimensional(dataset: IDataTableData[]): IDataTableData[] {
        /** Transpose it to a sequence of single valued series */
        if (dataset.length === 1 && dataset[0].key === ChartConstants.oneDimensionalToken) {

            var replacement: IDataTableData[] = [],
                series = dataset[0].value;
            for (var i = 0, l = series.length; i < l; i++) {
                replacement[i] = {
                    key: series[i].key, value: [{
                        key: ChartConstants.oneDimensionalToken,
                        value: series[i].value,
                        customData: series[i].customData
                    }]
                }
            }
            return replacement;
        } else {
            return dataset;
        }
    }

    private static _transpose(dataset: IDataTableData[]): IDataTableData[] {
        var replacement: IDataTableData[] = [],
            replacementSeries;
        for (var i = 0, l = dataset[0].value.length; i < l; i++) { //Group Field
            replacementSeries = [];

            for (var j = 0, m = dataset.length; j < m; j++) { //Date

                replacementSeries[j] = {
                    key: dataset[j].key,
                    value: dataset[j].value[i].value,
                    customData: dataset[j].value[i].customData
                };
            }

            replacement[i] = {
                key: dataset[0].value[i].key,
                value: replacementSeries
            };
        }
        return replacement;
    }

    public static cleanKey(keyName: string): string {
        /** Present the supplied key name in a user friendly fashion */
        var serverNullKey = "[NULL]";
        return (keyName !== null
            && typeof keyName !== "undefined"
            && keyName !== serverNullKey
            && keyName !== "NULL") ? keyName : Resources.ChartDataNullValueLabel;
    }

    public width: number;
    public height: number;

    public seriesNames: string[];
    public groupNames: string[];
    public seriesTotals: number[];
    public seriesValues: any[];
    public groupTotals: number[];
    public grandTotal: number;
    public maxValue: number;

    private _useSeriesColoring: boolean | undefined;
}
